import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { showErrorToast, showSuccessToast } from '../../../../components/PluginsMenu/Toastr/Toastr';
import { useUpdatePasswordMutation } from '../../../../../store/services/tutor/DetailsService';
import hidePasswordIcon from "../../../../../assets/icons/hide-password.svg";
import showPasswordIcon from "../../../../../assets/icons/show-password.svg";

// Yup schema for validation
const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required('Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/\d/, 'Must contain at least one number'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

export const TutorPersonalDetails = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [updatePassword, { isLoading }] = useUpdatePasswordMutation();

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await updatePassword({
          OldPassword: values.currentPassword,
          NewPassword: values.newPassword,
          ConfirmPassword: values.confirmPassword,
        }).unwrap();

        if (response?.succeeded) {
          showSuccessToast('Password updated successfully!');
          resetForm();
        } else {
          showErrorToast(response?.message || 'Failed to update password');
        }
      } catch (error) {
        console.error('Update password error:', error);
        showErrorToast(error?.data?.errors?.[0] || 'Failed to update password');
      }
    },
  });

  return (
    <div className="row">
      <div className="col-xl-12 col-lg-8">
        <div className="card h-auto">
          <div className="card-header">
            <h6 className="title">Update Password</h6>
          </div>
          <div className="card-body">
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <div className="position-relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      className={`form-control ${formik.touched.currentPassword && formik.errors.currentPassword ? 'text-danger' : ''}`}
                      {...formik.getFieldProps('currentPassword')}
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <img src={hidePasswordIcon} height={18} />
                      ) : (
                        <img src={showPasswordIcon} height={18} />
                      )}
                    </button>
                  </div>
                  {formik.touched.currentPassword && formik.errors.currentPassword && (
                    <div className="text-danger">{formik.errors.currentPassword}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <div className="position-relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className={`form-control ${formik.touched.newPassword && formik.errors.newPassword ? 'text-danger' : ''}`}
                      {...formik.getFieldProps('newPassword')}
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <img src={hidePasswordIcon} height={18} />
                      ) : (
                        <img src={showPasswordIcon} height={18} />
                      )}
                    </button>
                  </div>
                  {formik.touched.newPassword && formik.errors.newPassword && (
                    <div className="text-danger">{formik.errors.newPassword}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Confirm Password</label>
                  <div className="position-relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className={`form-control ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'text-danger' : ''}`}
                      {...formik.getFieldProps('confirmPassword')}
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <img src={hidePasswordIcon} height={18} />
                      ) : (
                        <img src={showPasswordIcon} height={18} />
                      )}
                    </button>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <div className="text-danger">{formik.errors.confirmPassword}</div>
                  )}
                </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};